import { Component, ChangeDetectionStrategy, inject, computed } from "@angular/core";
import { toSignal } from "@angular/core/rxjs-interop";
import { TranslateModule } from "@ngx-translate/core";
import { Observable } from "rxjs";

import { DynamicDialogRef, DynamicDialogConfig } from "primeng/dynamicdialog";
import { ButtonModule } from "primeng/button";

export interface ProgressDialogData {
    title: string;
    message: string;
    status: 'PENDING' | 'SUCCESS' | 'FAILURE';
    response?: string;
}

interface DialogConfig {
    data$: Observable<ProgressDialogData | null>;
}

@Component({
    selector: "app-progress-dialog",
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [ButtonModule, TranslateModule],
    template: `
        @if (data(); as d) {
            <div class="flex flex-column align-items-center gap-3 p-4">
                <i [class]="icon()" [style.color]="iconColor()"></i>

                <h3 class="text-xl font-semibold m-0 text-center">{{ d.title }}</h3>
                <p class="text-secondary text-center m-0">{{ d.message }}</p>

                @if (d.response) {
                    <p class="font-semibold m-0" style="white-space: pre-line">{{ d.response }}</p>
                }

                @if (d.status !== 'PENDING') {
                    <div class="mt-2">
                        <p-button [label]="'CLOSE' | translate" (onClick)="ref.close()" />
                    </div>
                }
            </div>
        }
    `,
})
export class ProgressDialogComponent {

    // =========================================================================
    // DEPENDENCY INJECTION
    // =========================================================================
    public readonly ref = inject(DynamicDialogRef);
    private readonly config = inject<DynamicDialogConfig<DialogConfig>>(DynamicDialogConfig);

    // =========================================================================
    // SIGNALS
    // =========================================================================
    public readonly data = toSignal(this.config.data!.data$);

    // =========================================================================
    // COMPUTED
    // =========================================================================
    public readonly icon = computed(() => {
        const status = this.data()?.status;
        switch (status) {
            case 'PENDING': return 'pi pi-spin pi-spinner text-5xl';
            case 'SUCCESS': return 'pi pi-check-circle text-5xl';
            case 'FAILURE': return 'pi pi-times-circle text-5xl';
            default: return '';
        }
    });

    public readonly iconColor = computed(() => {
        const status = this.data()?.status;
        switch (status) {
            case 'PENDING': return 'var(--p-primary-color)';
            case 'SUCCESS': return 'var(--p-green-500)';
            case 'FAILURE': return 'var(--p-red-500)';
            default: return '';
        }
    });
}
